import { OfferHistory } from '../../../../shared/models/offer-history.model';
import { get } from 'lodash';
import { Component, Input } from '@angular/core';
import { BidHistory, LaunchDarklyService, DateUtilsService } from 'remarketing-angular-library';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { CurrencyFormatService, OfferStatusPipe } from 'smartauction-frontend-micro-library';
import { CommonModule } from '@angular/common';
import { ParseStringPipe } from 'src/shared/ui/pipes/parse-string/parse-string.pipe';

@Component({
    selector: 'app-offer-history',
    templateUrl: './offer-history.component.html',
    styleUrls: ['./offer-history.component.scss'],
    imports: [TranslocoDirective, CommonModule, OfferStatusPipe, ParseStringPipe]
})
export class OfferHistoryComponent {
  @Input() offerHistory: OfferHistory[];
  @Input() bidHistory: { [key: string]: BidHistory[] };
  @Input() vin: string;

  /**
   * Renders the offer chain behind a collapsed disclosure instead of always expanded (M4.8).
   *
   * Defaults to false so the other six hosts -- cancel-offer, follow-up-offer, counter-offer, both
   * shared-list modals and bid-history-modal -- are untouched. Only the View Offer modals the comps
   * redesigned opt in, and they do it on the pricing flag, so a tenant with all-in pricing off still
   * sees today's expanded history.
   */
  @Input() collapsible = false;

  /**
   * Renders the VIN / DATE / AMOUNT / STATUS strip above the chain.
   *
   * Defaults to true, so the seven hosts that do not pass it are untouched. Only Follow-Up Offer passes
   * it, opting out on the pricing flag (initial-offer does not host this component at all): the Make
   * Offer comps (Figma 10360:9077 and 10493:16256) carry no strip, because the vehicle is now named by
   * the identification block the template renders above this component and the amount by the all-in
   * breakdown below it -- the strip would restate both and contradict neither usefully, since its
   * AMOUNT is the offer as it stands and the breakdown's is the offer being made.
   *
   * A separate input rather than folding it into `collapsible`: the View Offer modals collapse the
   * chain and KEEP the strip (Figma 10493:16426), so one flag cannot answer both questions.
   */
  @Input() showSummary = true;

  /**
   * Collapsed to start, per the comps: with the flag on, the breakdown and the binding-offer prose are
   * what the buyer needs above the fold, and the chain is reference material.
   *
   * Ignored entirely when collapsible is false -- the panel renders open regardless -- so flipping this
   * cannot hide history from a host that never asked for a disclosure.
   */
  historyExpanded = false;

  toggleHistory() {
    this.historyExpanded = !this.historyExpanded;
  }

  constructor(
    readonly dateUtilService: DateUtilsService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly translocoService: TranslocoService,
    private readonly currencyFormatService: CurrencyFormatService
  ) {}
  /**
   * We assume that we only care about the first item in the offer history, since
   * each user should only have one active offer on a vehicle at a time. Will need to
   * discuss further.
   */
  get offerChain() {
    // If there is no offerUuid, then this offer chain is not an actual offer that should be displayed.
    return this.offerHistory?.length && this.offerHistory[0]?.offer?.offerUuid ? this.offerHistory[0] : undefined;
  }

  get rootOffer() {
    return this.offerChain?.offer;
  }

  get previousOffers() {
    return this.offerChain?.previousOffers;
  }

  get fakeOfferStatus() {
    if ((window as any).SA_FAKE_OFFER_STATUS) {
      return (window as any).SA_FAKE_OFFER_STATUS;
    }
    return get(this.offerHistory, '[0].offer.status', '');
  }

  get isCanadianTenantEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get frenchCanadian(): boolean {
    const frenchLanguage = this.translocoService.getActiveLang().includes('fr');
    return frenchLanguage && this.isCanadianTenantEnabled;
  }

  formatCurrency(currency) {
    return this.currencyFormatService.formatCurrency(currency, false).replaceAll('-', '');
  }

  localizeDate(date) {
    return this.dateUtilService.localizeDate(date);
  }

  formatTime(time) {
    return `${this.dateUtilService.localizeTime(time)} ET`;
  }
}
